import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  LoginRequest,
  LoginResponse,
  User,
  UserCreateInput,
  UserActivityLog,
  UserStats,
  Media,
  Device,
  DeviceCreate,
  DeviceUpdate,
  DeviceGroup,
  Playlist,
  PlaylistItem,
  Schedule,
  ScheduleEvent,
  ScheduleWithEvents,
  ScheduleEventWithDetails,
  ScheduleStats,
  ScheduleConflict,
  CalendarEvent,
  ScheduleCreate,
  ScheduleUpdate,
  ScheduleEventCreate,
  ScheduleEventUpdate,
  PaginatedResponse,
  ApiError,
  DashboardStats,
  SearchFilters,
  SortOptions,
  EventStatus,
  WebPlayerCreate,
  WebPlayerCreated,
  WebPlayerLink,
  PlaybackSyncCreate,
  PlaybackSyncSession,
  AssignmentsOverview,
  DeviceAvailabilityCalendar,
} from '../types/index';
import { environmentConfig } from '../config/environment';

console.log('🎯 API Service initialized with config:', environmentConfig);

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    const baseURL = environmentConfig.isProduction
      ? `${environmentConfig.apiBaseUrl}/api/v1`  // Production: /api/v1
      : `${environmentConfig.apiBaseUrl}/api/v1`; // Development: hostname:8000/api/v1

    console.log('⚙️ Создаем ApiClient с baseURL:', baseURL);
    console.log('🏗️ Environment:', environmentConfig.isProduction ? 'Production' : 'Development');

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor для добавления токена
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Interceptor для обработки ошибок
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Токен истек, очищаем его
          this.clearToken();
          // Можно добавить редирект на логин
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Восстанавливаем токен из localStorage
    this.loadTokenFromStorage();
  }

  // Управление токеном
  setToken(token: string) {
    this.token = token;
    // Унифицируем ключ хранения токена
    localStorage.setItem('access_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  private loadTokenFromStorage() {
    // Поддерживаем обратную совместимость: читаем оба ключа
    const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
    if (token) {
      this.token = token;
      // Перекладываем в единый ключ
      localStorage.setItem('access_token', token);
      localStorage.removeItem('auth_token');
    }
  }

  // Вспомогательные методы
    private async request<T>(methodOrConfig: string | AxiosRequestConfig, url?: string, data?: any): Promise<T> {
    try {
      let config: AxiosRequestConfig;

      // Обратная совместимость: поддерживаем старый формат {method, url, data} и новый (method, url, data)
      if (typeof methodOrConfig === 'string') {
        // Новый формат: request('GET', '/path', data)
        config = {
          method: methodOrConfig as any,
          url: url!,
          data
        };
      } else {
        // Старый формат: request({method: 'GET', url: '/path', data: ...})
        config = methodOrConfig;
      }

      // Для URLSearchParams принудительно устанавливаем правильный Content-Type
      if (config.data instanceof URLSearchParams) {
        config.headers = {
          ...config.headers,
          'Content-Type': 'application/x-www-form-urlencoded'
        };
        console.log('🔧 DEBUG HEADERS:', config.headers);
      } else if (config.data instanceof FormData) {
        config.headers = {
          ...config.headers,
          // Для FormData не устанавливаем Content-Type - браузер сделает это автоматически
        };
      }

      const response: AxiosResponse<T> = await this.client.request(config);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data as ApiError;
      }
      throw new Error(error.message || 'Network error');
    }
  }

  private buildParams(filters?: SearchFilters, sort?: SortOptions, page?: number, pageSize?: number) {
    const params: Record<string, any> = {};

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value;
        }
      });
    }

    if (sort) {
      params.sort_by = sort.field;
      params.sort_order = sort.direction;
    }

    if (page !== undefined) {
      params.page = page;
    }

    if (pageSize !== undefined) {
      params.size = pageSize;
    }

    return params;
  }

  private normalizePaginatedResponse<T>(response: any, page: number, pageSize: number): PaginatedResponse<T> {
    const total = response?.total || 0;
    return {
      items: response?.items || [],
      total,
      page: response?.page || page,
      page_size: response?.page_size || response?.size || pageSize,
      total_pages: response?.total_pages || response?.pages || Math.ceil(total / pageSize),
    };
  }

  // АУТЕНТИФИКАЦИЯ
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // OAuth2PasswordRequestForm ожидает application/x-www-form-urlencoded
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('username', credentials.username);
    urlEncodedData.append('password', credentials.password);

    console.log('🔧 DEBUG LOGIN:', {
      url: '/auth/login',
      data: urlEncodedData.toString(),
      type: typeof urlEncodedData,
      isURLSearchParams: urlEncodedData instanceof URLSearchParams
    });

    const response = await this.request<LoginResponse>('POST', '/auth/login', urlEncodedData);

    // Сохраняем токены
    this.setToken(response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('POST', '/auth/logout');
    } finally {
      this.clearToken();
    }
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('GET', '/auth/me');
  }

  async refreshToken(): Promise<LoginResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.request<LoginResponse>('POST', '/auth/refresh', { refresh_token: refreshToken });

    this.setToken(response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);

    return response;
  }

  // МЕДИАФАЙЛЫ
  async getMedia(filters?: SearchFilters, sort?: SortOptions, page = 1, pageSize = 20): Promise<PaginatedResponse<Media>> {
    const url = '/media/?' + new URLSearchParams(this.buildParams(filters, sort, page, pageSize)).toString();
    return this.request<PaginatedResponse<Media>>('GET', url);
  }

  async getMediaById(id: number): Promise<Media> {
    return this.request<Media>({
      method: 'GET',
      url: `/media/${id}`,
    });
  }

  async uploadMedia(file: File, title: string, description?: string): Promise<Media> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    if (description) {
      formData.append('description', description);
    }

    return this.request<Media>({
      method: 'POST',
      url: '/media/upload',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async deleteMedia(id: number): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/media/${id}`,
    });
  }

  getMediaDownloadUrl(id: number): string {
    const baseURL = environmentConfig.isProduction
      ? `${environmentConfig.apiBaseUrl}/api/v1`
      : `${environmentConfig.apiBaseUrl}/api/v1`;
    return `${baseURL}/media/${id}/download`;
  }

  async getMediaBlob(id: number): Promise<Blob> {
    const response = await this.client.request<Blob>({
      method: 'GET',
      url: `/media/${id}/download`,
      responseType: 'blob',
    });
    return response.data;
  }

  getMediaThumbnailUrl(id: number): string {
    const baseURL = environmentConfig.isProduction
      ? `${environmentConfig.apiBaseUrl}/api/v1`
      : `${environmentConfig.apiBaseUrl}/api/v1`;
    return `${baseURL}/media/${id}/thumbnail`;
  }

  // === УСТРОЙСТВА ===

  // Получение списка устройств
  async getDevices(params?: {
    page?: number;
    size?: number;
    query?: string;
    device_type?: string;
    status?: string;
    group_id?: number;
    location?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_order?: string;
  }): Promise<PaginatedResponse<Device>> {
    return this.request<PaginatedResponse<Device>>({
      method: 'GET',
      url: '/devices',
      params,
    });
  }

  // Получение устройства по ID
  async getDevice(id: number): Promise<Device> {
    return this.request('GET', `/devices/${id}`);
  }

  // Создание устройства (регистрация)
  async createDevice(deviceData: DeviceCreate): Promise<Device> {
    return this.request('POST', '/devices/register', deviceData);
  }

  // Обновление устройства
  async updateDevice(id: number, deviceData: DeviceUpdate): Promise<Device> {
    return this.request('PUT', `/devices/${id}`, deviceData);
  }

  // Удаление устройства
  async deleteDevice(id: number, forceDelete: boolean = false): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/devices/${id}`,
      data: { force: forceDelete },
    });
  }

  // === НОВЫЕ МЕТОДЫ УСТРОЙСТВ 27.07.2025 ===

  // Получение системной информации устройства
  async getDeviceSystemInfo(id: number): Promise<{
    device_info: Device;
    system_settings: Record<string, any>;
    recent_commands: any[];
    playback_stats_24h: {
      total_plays: number;
      avg_response_time: number;
    };
    uptime_hours: number;
  }> {
    return this.request('GET', `/devices/${id}/system-info`);
  }

  // Обновление настроек устройства
  async updateDeviceSettings(id: number, settings: Record<string, any>): Promise<{
    message: string;
    updated_settings: Record<string, any>;
  }> {
    return this.request('PUT', `/devices/${id}/settings`, settings);
  }

  // Получение истории команд устройства
  async getDeviceCommandHistory(id: number, params?: {
    limit?: number;
    command_type?: string;
  }): Promise<{
    device_id: number;
    commands: Array<{
      id: number;
      command_type: string;
      command_data: Record<string, any>;
      status: string;
      created_at: string;
      executed_at: string | null;
      result: Record<string, any> | null;
      error_message: string | null;
    }>;
  }> {
    return this.request({
      method: 'GET',
      url: `/devices/${id}/commands/history`,
      params,
    });
  }

  // Получение метрик производительности устройства
  async getDeviceMetrics(id: number, hours: number = 24): Promise<{
    device_id: number;
    period_hours: number;
    uptime_percentage: number;
    command_statistics: Array<{
      status: string;
      count: number;
      avg_execution_time_seconds: number;
    }>;
    current_system_load: Record<string, any>;
    last_heartbeat: string | null;
    status: string;
  }> {
    return this.request({
      method: 'GET',
      url: `/devices/${id}/metrics`,
      params: { hours },
    });
  }

  async getDeviceAvailabilityCalendar(id: number, params?: {
    date_from?: string;
    date_to?: string;
    timezone_name?: string;
  }): Promise<DeviceAvailabilityCalendar> {
    return this.request({
      method: 'GET',
      url: `/devices/${id}/availability-calendar`,
      params,
    });
  }

  // Перезагрузка устройства
  async restartDevice(id: number, force: boolean = false): Promise<{
    command_id: number;
    message: string;
    status: string;
  }> {
    return this.request('POST', `/devices/${id}/restart`, force);
  }

  // Обновление ПО устройства
  async updateDeviceSoftware(id: number, version?: string): Promise<{
    command_id: number;
    message: string;
    status: string;
  }> {
    return this.request('POST', `/devices/${id}/update-software`, version || null);
  }

  // Массовое обновление устройств
  async bulkUpdateDevices(deviceIds: number[], updateData: Record<string, any>): Promise<{
    updated_devices: number;
    message: string;
    device_ids: number[];
  }> {
    return this.request('PUT', '/devices/bulk-update', {
      device_ids: deviceIds,
      update_data: updateData
    });
  }

  // Отправка команды устройству
  async sendDeviceCommand(id: number, command: {
    command_type: string;
    command_data?: Record<string, any>;
  }): Promise<{
    command_id: number;
    status: string;
    message: string;
  }> {
    // Backend ожидает в теле device_id, иначе 422
    const body = { device_id: id, ...command };
    return this.request('POST', `/devices/${id}/commands`, body);
  }

  // Массовая отправка команд
  async sendBulkDeviceCommands(params: {
    device_ids?: number[];
    group_ids?: number[];
    command_type: string;
    command_data?: Record<string, any>;
  }): Promise<{
    commands_sent: number;
    devices_count?: number;
    groups_count?: number;
    command_ids?: number[];
    device_ids?: number[];
    message: string;
  }> {
    return this.request('POST', '/devices/commands/bulk', params);
  }

  async createPlaybackSyncSession(data: PlaybackSyncCreate): Promise<PlaybackSyncSession> {
    return this.request<PlaybackSyncSession>('POST', '/playback-sync/sessions', data);
  }

  async getPlaybackSyncSessions(params?: {
    page?: number;
    size?: number;
    status?: string;
    playlist_id?: number;
    device_id?: number;
    schedule_event_id?: number;
  }): Promise<PaginatedResponse<PlaybackSyncSession>> {
    return this.request<PaginatedResponse<PlaybackSyncSession>>({
      method: 'GET',
      url: '/playback-sync/sessions',
      params,
    });
  }

  async cancelPlaybackSyncSession(id: number): Promise<PlaybackSyncSession> {
    return this.request<PlaybackSyncSession>('POST', `/playback-sync/sessions/${id}/cancel`, {});
  }

  async resyncPlaybackSyncSession(id: number, data: { device_ids?: number[]; start_delay_seconds?: number }): Promise<PlaybackSyncSession> {
    return this.request<PlaybackSyncSession>('POST', `/playback-sync/sessions/${id}/resync`, data);
  }

  // Получение статистики устройств
  async getDeviceStats(): Promise<{
    total_devices: number;
    online_devices: number;
    offline_devices: number;
    devices_in_maintenance: number;
    by_type: Record<string, number>;
    by_status: Record<string, number>;
    recent_activity: Device[];
  }> {
    return this.request('GET', '/devices/stats');
  }

  // === ГРУППЫ УСТРОЙСТВ ===

  // Получение списка групп устройств
  async getDeviceGroups(): Promise<DeviceGroup[]> {
    return this.request('GET', '/devices/groups');
  }

  // Создание группы устройств
  async createDeviceGroup(groupData: {
    name: string;
    description?: string;
  }): Promise<DeviceGroup> {
    return this.request('POST', '/devices/groups', groupData);
  }

  // Получение группы с устройствами
  async getDeviceGroup(id: number): Promise<DeviceGroup & { devices: Device[] }> {
    return this.request('GET', `/devices/groups/${id}`);
  }

  // Обновление группы устройств
  async updateDeviceGroup(id: number, groupData: {
    name?: string;
    description?: string;
  }): Promise<DeviceGroup> {
    return this.request('PUT', `/devices/groups/${id}`, groupData);
  }

  // Удаление группы устройств
  async deleteDeviceGroup(id: number): Promise<void> {
    return this.request('DELETE', `/devices/groups/${id}`);
  }

  // Добавление устройства в группу
  async addDeviceToGroup(groupId: number, deviceId: number): Promise<{
    message: string;
  }> {
    return this.request('POST', `/devices/groups/${groupId}/devices/${deviceId}`);
  }

  // Удаление устройства из группы
  async removeDeviceFromGroup(groupId: number, deviceId: number): Promise<{
    message: string;
  }> {
    return this.request('DELETE', `/devices/groups/${groupId}/devices/${deviceId}`);
  }

  // Получение статистики групп
  async getDeviceGroupStats(): Promise<{
    groups: Array<{
      group_id: number;
      group_name: string;
      devices_count: number;
      online_count: number;
    }>;
  }> {
    return this.request('GET', '/devices/groups/stats');
  }

  // === WEB-PLAYERS ===

  async createWebPlayer(data: WebPlayerCreate): Promise<WebPlayerCreated> {
    return this.request<WebPlayerCreated>({
      method: 'POST',
      url: '/web-players',
      data,
    });
  }

  async getWebPlayers(page = 1, size = 50): Promise<PaginatedResponse<WebPlayerLink>> {
    return this.request<PaginatedResponse<WebPlayerLink>>({
      method: 'GET',
      url: '/web-players',
      params: { page, size },
    });
  }

  async getWebPlayer(id: number): Promise<WebPlayerLink> {
    return this.request<WebPlayerLink>({
      method: 'GET',
      url: `/web-players/${id}`,
    });
  }

  async rotateWebPlayerToken(id: number): Promise<WebPlayerCreated> {
    return this.request<WebPlayerCreated>({
      method: 'POST',
      url: `/web-players/${id}/rotate-token`,
    });
  }

  async revokeWebPlayer(id: number): Promise<WebPlayerLink> {
    return this.request<WebPlayerLink>({
      method: 'POST',
      url: `/web-players/${id}/revoke`,
    });
  }

  async deleteWebPlayer(id: number): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/web-players/${id}`,
    });
  }

  async getAssignmentsOverview(): Promise<AssignmentsOverview> {
    return this.request<AssignmentsOverview>({
      method: 'GET',
      url: '/assignments/overview',
    });
  }

  // ============ НОВЫЕ МЕТОДЫ (4 августа 2025) ============

  // === ЛОГИ УСТРОЙСТВ ===

  // Получение логов устройства
  async getDeviceLogs(id: number, params?: {
    lines?: number;
    log_type?: 'system' | 'player' | 'all';
    refresh?: boolean;
  }): Promise<{
    device_id: number;
    log_type: string;
    total_lines: number;
    entries: Array<{
      timestamp: string;
      level: string;
      message: string;
      source: string;
    }>;
    retrieved_at: string;
    source?: 'device' | 'cache';
    cached?: boolean;
    command_id?: number;
  }> {
    return this.request({
      method: 'GET',
      url: `/devices/${id}/logs`,
      params,
    });
  }

  // Очистка логов устройства
  async clearDeviceLogs(id: number, params: {
    log_type?: 'system' | 'player' | 'all';
    confirm: boolean;
  }): Promise<{
    status: string;
    message: string;
    command_id?: number;
    device_id: number;
    log_type: string;
    cleared_at: string;
  }> {
    return this.request('DELETE', `/devices/${id}/logs`, params);
  }

  // === СНИМКИ ЭКРАНА ===

  // Создание снимка экрана
  async takeScreenshot(id: number, params?: {
    quality?: number;
    width?: number;
    height?: number;
    return_base64?: boolean;
  }): Promise<{
    device_id: number;
    filename: string;
    file_size: number;
    quality: number;
    created_at: string;
    base64_data?: string;
    mime_type?: string;
  }> {
    return this.request('POST', `/devices/${id}/screenshot`, params || {});
  }

  // Получение списка снимков экрана
  async getScreenshots(id: number, params?: {
    limit?: number;
  }): Promise<{
    device_id: number;
    screenshots: Array<{
      filename: string;
      file_size: number;
      quality: number;
      created_at: string;
      base64_data?: string;
      mime_type?: string;
    }>;
    total_count: number;
    total_size: number;
  }> {
    return this.request({
      method: 'GET',
      url: `/devices/${id}/screenshots`,
      params,
    });
  }

  async getClientDownloads(): Promise<{
    items: Array<{
      platform: string;
      filename: string;
      download_url: string;
      version?: string;
      title?: string;
      description?: string;
    }>;
  }> {
    return this.request({
      method: 'GET',
      url: '/client-downloads',
    });
  }

  // === ПРОДВИНУТЫЕ КОМАНДЫ ВОСПРОИЗВЕДЕНИЯ ===

  // Перемотка воспроизведения
  async seekDevice(id: number, position: number): Promise<{
    status: string;
    message: string;
    device_id: number;
    position: number;
    sent_at: string;
  }> {
    return this.request('POST', `/devices/${id}/commands/seek`, { position });
  }

  // Установка громкости
  async setDeviceVolume(id: number, volume: number): Promise<{
    status: string;
    message: string;
    device_id: number;
    volume: number;
    sent_at: string;
  }> {
    return this.request('POST', `/devices/${id}/commands/volume`, { volume });
  }

  // Выключение/включение звука
  async muteDevice(id: number, mute: boolean = true): Promise<{
    status: string;
    message: string;
    device_id: number;
    mute: boolean;
    sent_at: string;
  }> {
    return this.request('POST', `/devices/${id}/commands/mute`, { mute });
  }

  // Следующий элемент плейлиста
  async nextMedia(id: number): Promise<{
    status: string;
    message: string;
    device_id: number;
    sent_at: string;
  }> {
    return this.request('POST', `/devices/${id}/commands/next`, {});
  }

  // Предыдущий элемент плейлиста
  async previousMedia(id: number): Promise<{
    status: string;
    message: string;
    device_id: number;
    sent_at: string;
  }> {
    return this.request('POST', `/devices/${id}/commands/previous`, {});
  }

  // ПЛЕЙЛИСТЫ
  async getPlaylists(filters?: SearchFilters, sort?: SortOptions, page = 1, pageSize = 20): Promise<PaginatedResponse<Playlist>> {
    return this.request<PaginatedResponse<Playlist>>({
      method: 'GET',
      url: '/playlists',
      params: this.buildParams(filters, sort, page, pageSize),
    });
  }

  async getPlaylistById(id: number): Promise<Playlist> {
    return this.request<Playlist>({
      method: 'GET',
      url: `/playlists/${id}`,
    });
  }

  async createPlaylist(data: Partial<Playlist>): Promise<Playlist> {
    return this.request<Playlist>({
      method: 'POST',
      url: '/playlists',
      data,
    });
  }

  async updatePlaylist(id: number, data: Partial<Playlist>): Promise<Playlist> {
    return this.request<Playlist>({
      method: 'PUT',
      url: `/playlists/${id}`,
      data,
    });
  }

  async deletePlaylist(id: number): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/playlists/${id}`,
    });
  }

  async duplicatePlaylist(id: number, data: { name: string; copy_items: boolean }): Promise<Playlist> {
    return this.request<Playlist>({
      method: 'POST',
      url: `/playlists/${id}/duplicate`,
      data,
    });
  }

  async addMediaToPlaylist(playlistId: number, mediaId: number, order?: number): Promise<void> {
    return this.request<void>({
      method: 'POST',
      url: `/playlists/${playlistId}/items`,
      data: { media_id: mediaId, order },
    });
  }

  async getPlaylistItems(playlistId: number): Promise<PlaylistItem[]> {
    return this.request<PlaylistItem[]>({
      method: 'GET',
      url: `/playlists/${playlistId}/items`,
    });
  }

  // ============ РАСПИСАНИЕ (SCHEDULES) ============

  // Расписания
  async getSchedules(filters?: SearchFilters, sort?: SortOptions, page = 1, pageSize = 20): Promise<PaginatedResponse<Schedule>> {
    const params: Record<string, any> = {
      page,
      size: pageSize,
      query: filters?.search || undefined,
      schedule_type: filters?.scheduleTypes?.length === 1 ? filters.scheduleTypes[0] : undefined,
      is_active: filters?.activeOnly ? true : undefined,
      created_by: filters?.createdBy || undefined,
      date_from: filters?.dateFrom || undefined,
      date_to: filters?.dateTo || undefined,
      sort_by: sort?.field || undefined,
      sort_order: sort?.direction || undefined,
    };
    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);

    const response = await this.request<any>({
      method: 'GET',
      url: '/schedules',
      params,
    });
    return this.normalizePaginatedResponse<Schedule>(response, page, pageSize);
  }

  async getScheduleStats(): Promise<ScheduleStats> {
    return this.request<ScheduleStats>({
      method: 'GET',
      url: '/schedules/stats',
    });
  }

  async getScheduleById(id: number): Promise<Schedule> {
    return this.request<Schedule>({
      method: 'GET',
      url: `/schedules/${id}`,
    });
  }

  async getScheduleWithEvents(id: number): Promise<ScheduleWithEvents> {
    return this.request<ScheduleWithEvents>({
      method: 'GET',
      url: `/schedules/${id}/details`,
    });
  }

  async createSchedule(data: ScheduleCreate): Promise<Schedule> {
    return this.request<Schedule>({
      method: 'POST',
      url: '/schedules',
      data,
    });
  }

  async updateSchedule(id: number, data: ScheduleUpdate): Promise<Schedule> {
    return this.request<Schedule>({
      method: 'PUT',
      url: `/schedules/${id}`,
      data,
    });
  }

  async startSchedule(id: number): Promise<{
    message: string;
    schedule_id: number;
    started_events: ScheduleEvent[];
    failed_events: Array<{ event_id: number; error?: string; errors?: any[] }>;
  }> {
    return this.request({
      method: 'POST',
      url: `/schedules/${id}/start`,
    });
  }

  async stopSchedule(id: number): Promise<{
    message: string;
    schedule_id: number;
    stopped_events: ScheduleEvent[];
    failed_events: Array<{ event_id: number; error?: string; errors?: any[] }>;
  }> {
    return this.request({
      method: 'POST',
      url: `/schedules/${id}/stop`,
    });
  }

  async deleteSchedule(id: number): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/schedules/${id}`,
    });
  }

  async getSchedulePreview(id: number, previewDays = 7): Promise<any> {
    return this.request<any>({
      method: 'GET',
      url: `/schedules/${id}/preview`,
      params: { preview_days: previewDays },
    });
  }

  // События расписания
  async getScheduleEvents(filters?: SearchFilters, sort?: SortOptions, page = 1, pageSize = 20): Promise<PaginatedResponse<ScheduleEvent>> {
    const params: Record<string, any> = {
      page,
      size: pageSize,
      query: filters?.search || undefined,
      schedule_id: filters?.scheduleId || undefined,
      status: filters?.eventStatuses?.length === 1 ? filters.eventStatuses[0] : undefined,
      priority: filters?.priorities?.length === 1 ? filters.priorities[0] : undefined,
      device_id: filters?.deviceId || undefined,
      device_group_id: filters?.deviceGroupId || undefined,
      date_from: filters?.dateFrom || undefined,
      date_to: filters?.dateTo || undefined,
      sort_by: sort?.field || undefined,
      sort_order: sort?.direction || undefined,
    };
    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);

    const response = await this.request<any>({
      method: 'GET',
      url: '/schedules/events',
      params,
    });
    return this.normalizePaginatedResponse<ScheduleEvent>(response, page, pageSize);
  }

  async createScheduleEvent(data: ScheduleEventCreate): Promise<ScheduleEvent> {
    return this.request<ScheduleEvent>({
      method: 'POST',
      url: '/schedules/events',
      data,
    });
  }

  async createScheduleEventForSchedule(scheduleId: number, data: ScheduleEventCreate): Promise<ScheduleEvent> {
    return this.request<ScheduleEvent>({
      method: 'POST',
      url: `/schedules/${scheduleId}/events`,
      data,
    });
  }

  async getScheduleEventById(id: number): Promise<ScheduleEvent> {
    return this.request<ScheduleEvent>({
      method: 'GET',
      url: `/schedules/events/${id}`,
    });
  }

  async getScheduleEventWithDetails(id: number): Promise<ScheduleEventWithDetails> {
    return this.request<ScheduleEventWithDetails>({
      method: 'GET',
      url: `/schedules/events/${id}/details`,
    });
  }

  async updateScheduleEvent(id: number, data: ScheduleEventUpdate): Promise<ScheduleEvent> {
    return this.request<ScheduleEvent>({
      method: 'PUT',
      url: `/schedules/events/${id}`,
      data,
    });
  }

  async deleteScheduleEvent(id: number): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/schedules/events/${id}`,
    });
  }

  async updateScheduleEventStatus(
    id: number,
    status: EventStatus,
    executionResult?: any,
    errorMessage?: string
  ): Promise<ScheduleEvent> {
    return this.request<ScheduleEvent>({
      method: 'PUT',
      url: `/schedules/events/${id}/status`,
      data: {
        status: status,
        execution_result: executionResult,
        error_message: errorMessage,
      },
    });
  }

  // Активные события и календарь
  async checkActiveEvents(): Promise<{ activated_events: ScheduleEvent[]; message: string }> {
    return this.request<{ activated_events: ScheduleEvent[]; message: string }>({
      method: 'GET',
      url: '/schedules/events/check-active',
    });
  }

  async checkScheduleConflicts(eventData: ScheduleEventCreate): Promise<ScheduleConflict[]> {
    return this.request<ScheduleConflict[]>({
      method: 'POST',
      url: '/schedules/events/conflicts',
      data: eventData,
    });
  }

  async getCalendarEvents(
    startDate: string,
    endDate: string,
    deviceIds?: number[],
    groupIds?: number[]
  ): Promise<{ events: CalendarEvent[] }> {
    const response = await this.request<{ events: any[] }>({
      method: 'GET',
      url: '/schedules/events/calendar',
      params: {
        start_date: startDate,
        end_date: endDate,
        device_ids: deviceIds,
        group_ids: groupIds,
      },
    });

    return {
      events: (response.events || []).map((event) => ({
        ...event,
        start_time: event.start_time || event.start,
        end_time: event.end_time || event.end,
        device: event.device || (event.device_name ? { id: 0, name: event.device_name } : undefined),
        playlist: event.playlist || (event.content_name ? { id: 0, name: event.content_name } : undefined),
      })),
    };
  }

  // События для устройства
  async getDeviceEvents(
    deviceId: number,
    startTime?: string,
    endTime?: string
  ): Promise<{ device_id: number; events_count: number; events: ScheduleEvent[] }> {
    return this.request<{ device_id: number; events_count: number; events: ScheduleEvent[] }>({
      method: 'GET',
      url: `/schedules/devices/${deviceId}/events`,
      params: {
        start_time: startTime,
        end_time: endTime,
      },
    });
  }

  // ОТЧЕТЫ И ДАШБОРД
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('GET', '/dashboard/stats');
  }

  async getActivityLogs(filters?: SearchFilters, page = 1, pageSize = 50) {
    return this.request({
      method: 'GET',
      url: '/reports/activity-logs',
      params: this.buildParams(filters, undefined, page, pageSize),
    });
  }

  // ПОЛЬЗОВАТЕЛИ (для администраторов)
  async getUsers(filters?: SearchFilters, sort?: SortOptions, page = 1, pageSize = 20): Promise<PaginatedResponse<User>> {
    const params: Record<string, any> = {
      page,
      size: pageSize,
      query: filters?.search || undefined,
      role: filters?.userRoles?.length === 1 ? filters.userRoles[0] : undefined,
      roles: filters?.userRoles && filters.userRoles.length > 1 ? filters.userRoles.join(',') : undefined,
      is_active: filters?.activeUsersOnly ? true : filters?.inactiveUsersOnly ? false : undefined,
      created_from: filters?.dateFrom || undefined,
      created_to: filters?.dateTo || undefined,
      last_login_from: filters?.onlineUsersOnly ? new Date(Date.now() - 15 * 60 * 1000).toISOString() : undefined,
      sort_by: sort?.field || undefined,
      sort_order: sort?.direction || undefined,
    };

    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);

    const response = await this.request<any>({
      method: 'GET',
      url: '/auth/users',
      params,
    });

    return {
      items: response.items || [],
      total: response.total || 0,
      page: response.page || page,
      page_size: response.page_size || response.size || pageSize,
      total_pages: response.total_pages || response.pages || 0,
    };
  }

  async createUser(data: UserCreateInput): Promise<User> {
    return this.request<User>({
      method: 'POST',
      url: '/auth/register',
      data,
    });
  }

  async updateUser(id: number, data: Partial<User>): Promise<User> {
    return this.request<User>({
      method: 'PUT',
      url: `/auth/users/${id}`,
      data,
    });
  }

  async changeUserRole(id: number, role: string): Promise<User> {
    return this.request<User>({
      method: 'PUT',
      url: `/auth/users/${id}/role`,
      data: { role },
    });
  }

  async activateUser(id: number): Promise<User> {
    return this.request<User>({
      method: 'PUT',
      url: `/auth/users/${id}/activate`,
    });
  }

  async deactivateUser(id: number): Promise<User> {
    return this.request<User>({
      method: 'PUT',
      url: `/auth/users/${id}/deactivate`,
    });
  }

  async deleteUser(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>({
      method: 'DELETE',
      url: `/auth/users/${id}`,
    });
  }

  async resetUserPassword(id: number, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>({
      method: 'POST',
      url: '/auth/reset-password',
      data: {
        user_id: id,
        new_password: newPassword,
      },
    });
  }

  async getUserStats(): Promise<UserStats> {
    return this.request<UserStats>({
      method: 'GET',
      url: '/auth/users/stats',
    });
  }

  async getUserActivity(id: number, page = 1, pageSize = 20): Promise<PaginatedResponse<UserActivityLog>> {
    const response = await this.request<any>({
      method: 'GET',
      url: `/auth/users/${id}/activity`,
      params: {
        page,
        size: pageSize,
      },
    });

    return {
      items: response.items || [],
      total: response.total || 0,
      page: response.page || page,
      page_size: response.page_size || response.size || pageSize,
      total_pages: response.total_pages || response.pages || 0,
    };
  }

  // === МЕТОДЫ КОНВЕРТАЦИИ ===

  // Получить профили конвертации
  async getConversionProfiles() {
    return this.request('GET', '/media/profiles');
  }

  // Инициировать конвертацию
  async convertMedia(mediaId: number, profileName: string, customSettings?: any) {
    return this.request('POST', `/media/${mediaId}/convert`, {
      media_id: mediaId,
      profile_name: profileName,
      custom_settings: customSettings
    });
  }

  // Получить историю конвертаций
  async getConversionHistory(mediaId: number) {
    return this.request('GET', `/media/${mediaId}/conversions`);
  }

  // Получить активные задачи конвертации
  async getActiveConversionJobs() {
    return this.request('GET', '/media/jobs');
  }

  // Отменить задачу конвертации
  async cancelConversionJob(jobId: string) {
    return this.request('DELETE', `/media/jobs/${jobId}`);
  }

  // Публичные HTTP методы для совместимости с registrationTokenApi
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...(config || {}),
      method: 'GET',
      url,
    });
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...(config || {}),
      method: 'POST',
      url,
      data,
    });
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...(config || {}),
      method: 'PUT',
      url,
      data,
    });
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...(config || {}),
      method: 'DELETE',
      url,
    });
  }
}

// Создаем единственный экземпляр API клиента
const apiClient = new ApiClient();

// РАДИКАЛЬНОЕ ИСПРАВЛЕНИЕ: экспортируем все возможные способы
export { apiClient };
export default apiClient;
// Добавляем для совместимости
export const api = apiClient;
